<script setup lang="ts">
  import { h } from 'vue'
  import DiscordIcon from '~icons/fa7-brands/discord'
  import PatreonIcon from '~icons/fa7-brands/patreon'
  import TwitchIcon from '~icons/fa7-brands/twitch'
  import FilterIcon from '~icons/fa7-solid/filter'
  import { widen } from '~shared/util/type'
  import TurnipExchangeDropdown, { type DropdownProps } from './TurnipExchangeDropdown.vue'

  type MapFilters<T extends readonly any[]> = {
    [K in T[number] as K['key']]: K['items'][number]['value']
  }

  export interface IslandFilterBarState extends Partial<MapFilters<typeof filters>> {}

  // Define the model for the parent to consume
  const model = defineModel<IslandFilterBarState>({
    default: () => {
      return {
        turnips: 'any',
        fruit: 'any',
        hemisphere: 'any',
        visitor: 'any',
        fees: 'any',
        privacy: 'any',
        sort: 'new',
      } satisfies IslandFilterBarState
    },
  })

  // Helper components for non-standard icons
  const AnyIslandIcon = () => h('img', { src: '/images/icons/any-island.svg', class: 'size-6' })
  const AnyFruitIcon = () => h('img', { src: '/images/icons/any-fruit.svg', class: 'size-6' })
  const AnyNPCIcon = () => h('img', { src: '/images/icons/any-npcs.svg', class: 'size-6' })
  const AnyFeeIcon = () => h('img', { src: '/images/icons/any-fee.svg', class: 'size-6' })
  const AnyLocationIcon = () => h('img', { src: '/images/icons/any-location.svg', class: 'size-6' })
  const AnyHostIcon = () => h('img', { src: '/images/icons/any-host.svg', class: 'size-6' })

  const TurnipIcon = () => h('img', { src: '/images/icons/turnip.svg', class: 'size-6' })
  const CatalogIcon = () => h('img', { src: '/images/icons/catalog.svg', class: 'size-6' })
  const CraftingIcon = () => h('img', { src: '/images/icons/crafting.svg', class: 'size-6' })
  const TreasureIcon = () => h('img', { src: '/images/icons/treasure.svg', class: 'size-6' })
  const EventIcon = () => h('img', { src: '/images/icons/event.svg', class: 'size-6' })

  const AppleIcon = () => h('img', { src: '/images/icons/apple.svg', class: 'size-6' })
  const CherryIcon = () => h('img', { src: '/images/icons/cherry.svg', class: 'size-6' })
  const OrangeIcon = () => h('img', { src: '/images/icons/orange.svg', class: 'size-6' })
  const PeachIcon = () => h('img', { src: '/images/icons/peach.svg', class: 'size-6' })
  const PearIcon = () => h('img', { src: '/images/icons/pear.svg', class: 'size-6' })
  const CoconutIcon = () => h('img', { src: '/images/icons/coconut.svg', class: 'size-6' })

  const CelesteIcon = () => h('img', { src: '/images/npc/celeste.svg', class: 'size-6' })
  const SaharahIcon = () => h('img', { src: '/images/npc/saharah.svg', class: 'size-6' })
  const ReddIcon = () => h('img', { src: '/images/npc/redd.svg', class: 'size-6' })
  const FlickIcon = () => h('img', { src: '/images/npc/flick.svg', class: 'size-6' })
  const CJIcon = () => h('img', { src: '/images/npc/cj.svg', class: 'size-6' })
  const LeifIcon = () => h('img', { src: '/images/npc/leif.svg', class: 'size-6' })
  const KicksIcon = () => h('img', { src: '/images/npc/kicks.svg', class: 'size-6' })
  const WispIcon = () => h('img', { src: '/images/npc/wisp.svg', class: 'size-6' })

  const NoFeeIcon = () => h('img', { src: '/images/icons/no-fee.svg', class: 'size-6' })
  const BellsIcon = () => h('img', { src: '/images/icons/bells.svg', class: 'size-6' })
  const NMTsIcon = () => h('img', { src: '/images/icons/nmt.svg', class: 'size-6' })

  const NorthIcon = () =>
    h(
      'span',
      {
        class:
          'flex size-5 items-center justify-center rounded-sm bg-yellow-400 text-[10px] font-bold text-black',
      },
      'N',
    )
  const SouthIcon = () => h('img', { src: '/images/icons/south-hemisphere.svg', class: 'size-6' })

  // Filter Definitions
  const filters = widen([
    {
      key: 'turnips',
      items: [
        { label: 'Any Type', value: 'any', icon: AnyIslandIcon },
        { label: 'Turnips', value: 'turnips', icon: TurnipIcon },
        { label: 'Treasure', value: 'treasure', icon: TreasureIcon },
        { label: 'Crafting', value: 'crafting', icon: CraftingIcon },
        { label: 'Event', value: 'event', icon: EventIcon },
        { label: 'Catalog', value: 'catalog', icon: CatalogIcon },
      ] as const,
    },
    {
      key: 'fruit',
      items: [
        { label: 'Any Fruit', value: 'any', icon: AnyFruitIcon },
        { label: 'Apple', value: 'apple', icon: AppleIcon },
        { label: 'Cherry', value: 'cherry', icon: CherryIcon },
        { label: 'Orange', value: 'orange', icon: OrangeIcon },
        { label: 'Peach', value: 'peach', icon: PeachIcon },
        { label: 'Pear', value: 'pear', icon: PearIcon },
        { label: 'Coconut', value: 'coconut', icon: CoconutIcon },
      ] as const,
    },
    {
      key: 'hemisphere',
      items: [
        { label: 'Global', value: 'any', icon: AnyLocationIcon },
        { label: 'North', value: 'north', icon: NorthIcon },
        { label: 'South', value: 'south', icon: SouthIcon },
      ] as const,
    },
    {
      key: 'visitor',
      items: [
        { label: 'Any NPC', value: 'any', icon: AnyNPCIcon },
        { label: 'Celeste', value: 'Celeste', icon: CelesteIcon },
        { label: 'Saharah', value: 'Saharah', icon: SaharahIcon },
        { label: 'Redd', value: 'Redd', icon: ReddIcon },
        { label: 'Flick', value: 'Flick', icon: FlickIcon },
        { label: 'C.J.', value: 'C.J.', icon: CJIcon },
        { label: 'Leif', value: 'Leif', icon: LeifIcon },
        { label: 'Kicks', value: 'Kicks', icon: KicksIcon },
        { label: 'Wisp', value: 'Wisp', icon: WispIcon },
      ] as const,
    },
    {
      key: 'fees',
      items: [
        { label: 'Any Fee', value: 'any', icon: AnyFeeIcon },
        { label: 'No Fee', value: 'none', icon: NoFeeIcon },
        { label: 'Bells', value: 'bells', icon: BellsIcon },
        { label: 'NMT', value: 'nmt', icon: NMTsIcon },
      ] as const,
    },
    {
      key: 'privacy',
      items: [
        { label: 'Any Hosts', value: 'any', icon: AnyHostIcon },
        { label: 'Twitch Sub', value: 'twitch-sub', icon: TwitchIcon },
        { label: 'Discord', value: 'discord', icon: DiscordIcon },
        { label: 'Patreon', value: 'patreon', icon: PatreonIcon },
      ] as const,
    },
    {
      key: 'sort',
      label: null,
      icon: FilterIcon,
      showChevron: false,
      items: [
        { label: 'Newest', value: 'new' },
        { label: 'Oldest', value: 'old' },
        { label: 'Rating', value: 'rating' },
      ] as const,
    },
  ] as const satisfies ({ key: string } & DropdownProps)[])
</script>

<template>
  <div class="grid w-full grid-cols-2 items-stretch justify-center gap-3 lg:flex lg:flex-nowrap">
    <TurnipExchangeDropdown
      v-for="filter in filters"
      :key="filter.key"
      v-model="model[filter.key as keyof typeof model]"
      :items="filter.items"
      :show-chevron="filter.showChevron"
      :icon="filter.icon"
      :label="filter.label"
    >
    </TurnipExchangeDropdown>
  </div>
</template>
